import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { buildVendorParam, extractVendorId } from "../utils/vendorSlug";
import { vendorView } from "../utils/analytics";
import styled from "styled-components";
import usePublishedHeight from "../hooks/usePublishedHeight";
import {
  SECTIONS_H_VAR,
  stickyUnderNav,
  sectionScrollMargin,
  scrollToSection,
} from "../utils/stickyStack";
import {
  moveCartToOutlet,
  setCurrentHealthcareVendor,
  setHealthcareData,
} from "../redux/healthcareCartSlice";
import placeholderImage from "../assets/img/menuImage.png";
import {
  applyBranchProductChange,
  fetchHealthcareVendorDetails,
} from "../redux/healthcareSlice";
/* Pharmacy branches (4 Oct 2026): the storefront opens on one branch
   (`?outletId=`, a HealthcareOutlet id), every product shows as it stands
   there, the shopper can choose another branch, and the cart holds one
   branch and is checked there. */
import {
  findOutlet,
  outletIdFromSearch,
  sortLocationsForPicker,
  vendorLocationsOf,
  withOutletQuery,
} from "../utils/vendorOutlets";
import { openNowOf } from "../utils/vendorOpenState";
import LocationPicker, { hereNameOf, locationName } from "../components/LocationPicker";
import useDeliveryPoint from "../hooks/useDeliveryPoint";
import { calculateDistance } from "../redux/userLocationSlice";
import useCatalogUpdates from "../hooks/useCatalogUpdates";
import { branchProductChanged, cartLocationOf } from "../utils/branchCart";
import {
  blockedHere,
  branchClosedNotice,
  locationKeyOf,
  outletIdOfKey,
} from "../utils/branchAvailability";
import BranchAvailabilityBadge from "../components/branch/BranchAvailabilityBadge";
import BranchCartReviewDialog from "../components/branch/BranchCartReviewDialog";
import { useCartBranchCheck } from "../hooks/useBranchCartCheck";
import useConfirmCartMove from "../hooks/useConfirmCartMove";
import OutletSwitchDialog from "../components/OutletSwitchDialog";
import HealthcareCart from "../components/HealthcareCart";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShareIcon from "@mui/icons-material/Share";
import LinkIcon from "@mui/icons-material/Link";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import { toast } from "react-toastify";
import { SEOHead, stripHtmlForSeo } from "../components/SEO/SEOHead";
import CrawlLink from "../components/CrawlLink";
import { shopPath, productPathUnder } from "../utils/seoPaths";
import {
  buildVendorSeoProps,
  buildVendorShareText,
  withVendorCacheBuster,
} from "../components/SEO/vendorSeo";
import healthcareBackgroundBanner from "../assets/vendorBackgroundBanner.png";
import HealthcareProductDialog from "../components/HealthcareProductDialog";
import HealthcareNavBar from "../components/HealthcareNavBar";
import Reviews from "../components/Reviews/Reviews";
import VendorWhatsAppFloat from "../components/VendorWhatsAppFloat";
import { SITE_ORIGIN, formatCurrency } from "../config/market";
import useCoverage from "../redux/useCoverage";
import useVendorAvailability from "../hooks/useRestaurantAvailability";
import CoverageBanner from "../components/CoverageBanner";
import RestaurantAvailabilityBanner from "../components/RestaurantAvailabilityBanner";
import LocationPrompt from "../components/LocationPrompt";
import DealTag from "../components/DealTag";
import PriceCut from "../components/PriceCut";
import { hasSalePrice } from "../utils/vendorDiscounts";
/* Healthcare on web was completely blind to price-on-request: this page printed
   `product.price` straight out, so a quote-only pharmacy line showed whatever
   number happened to be on the document, and a price the server nulls showed as
   a confident zero. Pharmacies sell both in bulk and at retail, so a quote-only
   line here is ordinary, not exotic. Same helper as the brand storefront and
   both customer apps, so one SKU cannot read differently on two surfaces. */
import {
  PRICE_ON_REQUEST_LABEL,
  PRICE_UNAVAILABLE_LABEL,
  isQuoteOnly,
  publishedPrice,
} from "../utils/priceOnRequest";
import {
  cardFooterRow,
  cardPriceRow,
  cardPriceOwnsItsRow,
} from "../components/productCardFooter";
import VendorTagline from "../components/VendorTagline";

const HealthcareShopPage = () => {
  const { id: idParam, productId: productParam } = useParams();
  const id = extractVendorId(idParam);
  /* /healthcare/shop/<pharmacy>/product/<product> opens that product's dialog
     over the storefront. It is the address search engines are given for a
     pharmacy product (src/utils/seoPaths.js), and the one a tap now puts in
     the address bar, so a product can be shared and found on its own. */
  const routeProductId = productParam ? extractVendorId(productParam) : null;
  /* True when this visit opened the product with a tap (so closing it goes
     back), false when it arrived on the product's own address. */
  const openedByTapRef = useRef(false);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const healthcareDetails = useSelector((state) => state.healthcare.details);
  const selectedActiveVendor = useSelector(
    (state) => state.healthcare.selectedVendor
  );
  /* The branch in the URL, and the one this storefront actually sells from:
     a branch that is switched off, deleted or not this pharmacy's reads as
     the pharmacy itself, as the server does. */
  const outletIdParam = outletIdFromSearch(location.search);
  const pharmacy = healthcareDetails?.vendor || selectedActiveVendor || null;
  const selectedOutlet = findOutlet(pharmacy, outletIdParam);
  const outletId = pharmacy && !selectedOutlet ? null : outletIdParam;
  const isFetching = useSelector((state) => state.healthcare.isFetching);
  const loadError = useSelector((state) => state.healthcare.error);
  const cart = useSelector((state) => state.healthcareCart.carts[id] || {});
  const cartCount = Object.values(cart).reduce(
    (total, item) => total + item.quantity,
    0
  );
  const sectionsNavRef = useRef(null);
  /* The strip's rendered height, for the scroll margins (utils/stickyStack). */
  usePublishedHeight(sectionsNavRef, SECTIONS_H_VAR);
  const [activeSection, setActiveSection] = useState(null);
  const [isCartVisible, setIsCartVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);

  // Pre-checkout coverage gate (backend /api/coverage) — surfaced here at
  // shop entry so the customer learns early, not at the last checkout step.
  const coverage = useCoverage({ vendorId: id, outletId, vendorType: "healthcare" });
  // Business hours. These verticals had no hours concept until the shared
  // availability service shipped; without this the vendor can mark themselves
  // closed in the dashboard and shoppers would never know.
  const availability = useVendorAvailability({
    vendorId: id,
    vendorType: "healthcare",
    outletId,
  });

  // ONE public fetch for this storefront: the pharmacy, its sections and its
  // products. `retryLoad` is the same call, so the error state below and this
  // mount share a single implementation.
  const retryLoad = useCallback(() => {
    if (id) dispatch(fetchHealthcareVendorDetails({ vendorId: id, outletId: outletIdParam }));
  }, [dispatch, id, outletIdParam]);

  useEffect(() => {
    if (id) {
      retryLoad();
      dispatch(setCurrentHealthcareVendor(id));
    }
  }, [dispatch, id, retryLoad]);

  // Durable, URL-independent vendor attribution (GA4 vendor_view).
  const vendorViewedRef = useRef(null);
  useEffect(() => {
    const vid = healthcareDetails?.vendor?._id || id;
    if (!vid || vendorViewedRef.current === String(vid)) return;
    vendorViewedRef.current = String(vid);
    vendorView({ vendorId: vid, vendorType: "healthcare" });
  }, [healthcareDetails?.vendor?._id, id]);

  // Mirror whatever pharmacy the store holds into the cart slice. A cold load
  // (shared link, refresh, return from a payment provider) has no pre-seeded
  // vendor in memory, so this used to run its own fetch against the admin-only
  // `?vendorId=` list endpoint and push the customer to "/" on the 403 that
  // came back. The fetch above serves both paths now, and a failure renders the
  // error state below instead of bouncing anyone off the storefront.
  useEffect(() => {
    if (selectedActiveVendor) {
      dispatch(setHealthcareData(selectedActiveVendor));
    }
  }, [selectedActiveVendor, dispatch]);

  useEffect(() => {
    const name = selectedActiveVendor?.name || healthcareDetails?.vendor?.name;
    if (!name) return;
    const canonical = buildVendorParam(name, id);
    if (idParam !== canonical) {
      // Keep the branch and the open product: the slug redirect must drop
      // neither the query string nor the /product/ part of the address.
      const productSuffix = productParam ? `/product/${productParam}` : "";
      history.replace(withOutletQuery(`/healthcare/shop/${canonical}${productSuffix}`, outletIdParam));
    }
  }, [selectedActiveVendor?.name, healthcareDetails?.vendor?.name, id, idParam, productParam, outletIdParam, history]);

  /* Every place this pharmacy sells from (the pharmacy itself and each
     active branch, each with its distance from the shopper), for the
     location picker and "Get it from". */
  const deliveryPoint = useDeliveryPoint();
  const pharmacyLocations = useMemo(
    () => vendorLocationsOf(pharmacy, { deliveryPoint, calculateDistance }),
    [pharmacy, deliveryPoint]
  );
  const pharmacyName = pharmacy?.tradingName || pharmacy?.name || "";
  // Named as the apps name it: a branch by its own name, the pharmacy itself
  // as "<pharmacy> (main branch)", and a one-shop pharmacy by its name alone.
  const nameOfLocation = (key) => {
    const outletKey = outletIdOfKey(key);
    const outlet = findOutlet(pharmacy, outletKey);
    if (outletKey && !outlet) return pharmacyName;
    return hereNameOf(pharmacyLocations, outlet, pharmacyName);
  };
  const hereName = nameOfLocation(outletId);

  /* The cart check at its branch: quiet while browsing, and "Changes at
     <branch>" right after the cart moves to another branch. */
  const { reviewNextCheck, dialogProps: branchDialogProps } = useCartBranchCheck({
    vertical: "healthcare",
    cartId: id,
    checkWhen: Boolean(healthcareDetails?.vendor),
    silent: true,
  });

  /* Open this pharmacy on another of its branches. ONE BRANCH PER CART: the
     cart moves along and every line is checked there. The picker asks first
     when the cart is on another branch, and so does "Get it from"
     (`getItFrom`, shopper journey audit 4 Oct 2026). */
  const cartLocation = cartLocationOf(cart);
  const cartBoundElsewhere = useCallback(
    (picked) => cartCount > 0 && cartLocation !== locationKeyOf(picked),
    [cartCount, cartLocation]
  );
  const chooseLocation = useCallback(
    (picked) => {
      const target = picked?.isOutlet ? String(picked._id) : null;
      if (cartCount > 0 && cartLocation !== locationKeyOf(picked)) {
        dispatch(moveCartToOutlet({ vendorId: id, outletId: target }));
        reviewNextCheck();
      }
      /* The storefront at the new branch, never an open product's address:
         switching branch closes the product, as it always has. */
      const storefront = location.pathname.replace(/\/product\/[^/]+\/?$/, "");
      history.push(withOutletQuery(storefront, target));
    },
    [cartCount, cartLocation, dispatch, history, id, location.pathname, reviewNextCheck]
  );
  const getItFrom = useConfirmCartMove({
    needsAsk: cartBoundElsewhere,
    onMove: chooseLocation,
  });

  /* The branch in the URL is not taking orders: switched off, deleted, or
     named by an old link. The page already reads it as the pharmacy itself,
     but said nothing, and a cart bought there stayed bound to it. Now it
     opens the best branch left, with that cart, and says why, as the brand
     storefront and the apps do. Once per branch. */
  const movedOffRef = useRef(null);
  useEffect(() => {
    if (!outletIdParam || !pharmacy || selectedOutlet) return;
    const mine = [pharmacy._id, pharmacy.owner].filter(Boolean).map(String);
    if (!mine.includes(String(id))) return;
    if (movedOffRef.current === String(outletIdParam)) return;
    const best = sortLocationsForPicker(pharmacyLocations, { isOpen: openNowOf })[0];
    if (!best) return;
    movedOffRef.current = String(outletIdParam);
    const target = best.isOutlet ? String(best._id) : null;
    const cartMoved = cartCount > 0 && cartLocation === locationKeyOf(outletIdParam);
    if (cartMoved) {
      dispatch(moveCartToOutlet({ vendorId: id, outletId: target }));
      reviewNextCheck();
    }
    const closed = (Array.isArray(pharmacy.outlets) ? pharmacy.outlets : []).find(
      (outlet) => String(outlet?._id) === String(outletIdParam)
    )?.name;
    const notice = branchClosedNotice({
      closed,
      next: locationName(best, pharmacyName),
      cartMoved,
    });
    toast.info(`${notice.title}. ${notice.message}`);
    history.replace(withOutletQuery(location.pathname, target));
  }, [
    outletIdParam,
    pharmacy,
    selectedOutlet,
    id,
    pharmacyLocations,
    cartCount,
    cartLocation,
    dispatch,
    reviewNextCheck,
    pharmacyName,
    history,
    location.pathname,
  ]);

  /* A branch of a pharmacy switched a product on or off, or sold it out
     (4 Oct 2026): the open storefront flips in place (the slice ignores
     other pharmacies) and any cart line it touches is checked again. */
  useCatalogUpdates(
    {
      "branchProduct:changed": (payload) => {
        dispatch(branchProductChanged(payload));
        if (payload.vertical && payload.vertical !== "healthcare") return;
        dispatch(applyBranchProductChange(payload));
      },
    },
    { enabled: Boolean(id) }
  );

  useEffect(() => {
    if (
      healthcareDetails &&
      healthcareDetails.sections &&
      healthcareDetails.sections.length > 0
    ) {
      const firstSectionWithProducts = healthcareDetails.sections.find(
        (section) => section.products && section.products.length > 0
      );
      if (firstSectionWithProducts) {
        setActiveSection(`section-${firstSectionWithProducts._id}`);
        setTimeout(() => {
          const sectionElement = document.getElementById(
            `section-${firstSectionWithProducts._id}`
          );
          if (sectionElement) {
            // Measured bars, not a viewport percentage; see utils/stickyStack.
            scrollToSection(
              sectionElement,
              document.querySelector("[data-sections-container]"),
              { pinStack: false }
            );
          }
        }, 100);
      }
    }
  }, [healthcareDetails]);

  // Scroll-spy: track which section is active based on window scroll position
  useEffect(() => {
    if (!healthcareDetails?.sections) return;

    const sectionsWithProducts = healthcareDetails.sections.filter(
      (section) => section.products && section.products.length > 0
    );

    if (sectionsWithProducts.length === 0) return;

    const handleScroll = () => {
      // Use the SectionsNav bottom as the reference point
      const navEl = sectionsNavRef.current;
      const navBottom = navEl
        ? navEl.getBoundingClientRect().bottom
        : 0;
      // Trigger line at 35% of viewport height below the nav
      const triggerLine = navBottom + window.innerHeight * 0.35;

      let currentActiveId = null;

      for (const section of sectionsWithProducts) {
        const element = document.getElementById(`section-${section._id}`);
        if (!element) continue;
        if (element.getBoundingClientRect().top <= triggerLine) {
          currentActiveId = `section-${section._id}`;
        }
      }

      if (!currentActiveId && sectionsWithProducts.length > 0) {
        currentActiveId = `section-${sectionsWithProducts[0]._id}`;
      }

      if (currentActiveId) {
        setActiveSection(currentActiveId);
      }
    };

    // Run once immediately
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [healthcareDetails]);

  // Auto-scroll the active section tab into view in the horizontal nav
  useEffect(() => {
    if (activeSection && sectionsNavRef.current) {
      const activeTab = sectionsNavRef.current.querySelector(
        `button[class*="active"], button:nth-child(${healthcareDetails?.sections
          ?.filter((s) => s.products && s.products.length > 0)
          ?.findIndex((s) => `section-${s._id}` === activeSection) + 1
        })`
      );
      if (activeTab) {
        activeTab.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [activeSection, healthcareDetails]);

  const handleSectionClick = (sectionId) => (event) => {
    event.preventDefault();
    const sectionElement = document.getElementById(sectionId);
    const container =
      sectionElement?.closest('[style*="overflow"]') ||
      document.querySelector("[data-sections-container]");

    if (sectionElement && container) {
      /* This page's container is plain flow, so `container.scrollTo` was a
         silent no-op here; the helper scrolls the window for it. */
      scrollToSection(sectionElement, container);
      setActiveSection(sectionId);
    } else if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(sectionId);
    }
  };

  const handleProductClick = (product) => {
    if (coverage.isOutOfRange) {
      toast.warn("This vendor doesn't deliver to your set location. Change your location or pick a vendor that delivers to you.");
      return;
    }
    setSelectedProduct(product);
    const productPagePath = productPathUnder(`/healthcare/shop/${idParam}`, product);
    if (productPagePath && String(product._id) !== String(routeProductId)) {
      openedByTapRef.current = true;
      history.push(withOutletQuery(productPagePath, outletIdParam));
    }
  };

  const handleCloseDialog = () => {
    setSelectedProduct(null);
    if (!routeProductId) return;
    if (openedByTapRef.current) {
      openedByTapRef.current = false;
      history.goBack();
    } else {
      history.replace(withOutletQuery(`/healthcare/shop/${idParam}`, outletIdParam));
    }
  };

  /* The address decides which product is open: arriving on a product's
     address opens it once the catalogue has loaded, and Back (which removes
     the /product/ part) closes it. */
  const routeProduct = useMemo(() => {
    if (!routeProductId) return null;
    for (const section of healthcareDetails?.sections || []) {
      const found = (section.products || []).find((p) => String(p._id) === String(routeProductId));
      if (found) return found;
    }
    return null;
  }, [routeProductId, healthcareDetails?.sections]);

  useEffect(() => {
    if (!routeProductId) {
      openedByTapRef.current = false;
      setSelectedProduct(null);
      return;
    }
    if (routeProduct) setSelectedProduct(routeProduct);
  }, [routeProductId, routeProduct]);

  const handleShareClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setAnchorEl(null);
  };

  /* Healthcare share — leads with the provider's name and lists up to 5
     featured products (medications/services) so the share preview tells
     the recipient what's actually on offer, not just "click here". */
  const buildHealthcareShareText = () => {
    const vendor = healthcareDetails?.vendor;
    if (!vendor) return null;
    const products = (healthcareDetails?.sections || [])
      .flatMap((s) => s.products?.filter((p) => p.enabled !== false) || [])
      .slice(0, 5);
    return buildVendorShareText(vendor, {
      url: withVendorCacheBuster(
        `${window.location.origin}/healthcare/shop/${buildVendorParam(vendor?.name, id)}`,
        vendor
      ),
      products,
      headerLabel: "Pharmacy",
    });
  };

  const handleCopyLink = () => {
    const text = buildHealthcareShareText();
    if (!text) return;
    navigator.clipboard.writeText(text)
      .then(() => toast.success("Link copied to clipboard!"))
      .catch(() => toast.error("Failed to copy"));
    handleShareClose();
  };

  const handleWhatsAppShare = () => {
    const text = buildHealthcareShareText();
    if (!text) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    handleShareClose();
  };

  const sectionsWithProducts =
    healthcareDetails?.sections?.filter(
      (section) => section.products && section.products.length > 0
    ) || [];

  // Nothing to show yet: the fetch is in flight, or it has not resolved and
  // has not failed. Holding the loader here is what stops the error card
  // flashing on the first frame.
  if (isFetching || (!loadError && !healthcareDetails?.vendor)) {
    return (
      <LoadingContainer>
        <p>Loading healthcare services...</p>
      </LoadingContainer>
    );
  }

  if (!healthcareDetails?.vendor) {
    return (
      <ErrorContainer>
        <HealthcareLoadError onRetry={retryLoad} />
      </ErrorContainer>
    );
  }

  return (
    <>
      {/* Vendor-first social card — was missing entirely from this page,
          which meant healthcare vendor links rendered as the generic
          Lusuku preview when shared. */}
      {routeProduct ? (
        <SEOHead
          title={routeProduct.name}
          description={
            stripHtmlForSeo(routeProduct.description || "").slice(0, 160) ||
            `Buy ${routeProduct.name} from ${healthcareDetails.vendor?.name || "Lusuku"}.`
          }
          imageUrl={routeProduct.image}
          canonicalPath={productPathUnder(
            shopPath("healthcare", healthcareDetails.vendor) ||
              `/healthcare/shop/${buildVendorParam(healthcareDetails.vendor?.name, id)}`,
            routeProduct,
          )}
          type="product"
          product={{
            _id: routeProduct._id,
            name: routeProduct.name,
            description: stripHtmlForSeo(routeProduct.description || ""),
            imageUrl: routeProduct.image,
            price: publishedPrice(routeProduct),
            vendorName: healthcareDetails.vendor?.name,
            available: routeProduct.enabled !== false,
          }}
        />
      ) : (
        <SEOHead {...buildVendorSeoProps(healthcareDetails.vendor, {
          canonicalPath:
            shopPath("healthcare", healthcareDetails.vendor) ||
            `/healthcare/shop/${buildVendorParam(healthcareDetails.vendor?.name, id)}`,
          vendorType: "healthcare",
        })} />
      )}
      <HealthcareNavBar
        vendor={healthcareDetails.vendor}
        cartCount={cartCount}
        onCartClick={() => setIsCartVisible(!isCartVisible)}
      />

      <PageContainer>
        <BannerContainer>
          <BannerImage
            src={
              healthcareDetails.vendor.shopBannerUrl ||
              healthcareDetails.vendor.bannerUrl ||
              healthcareBackgroundBanner
            }
            alt="Healthcare Banner"
          />
          <VendorInfoOverlay>
            <VendorAvatar
              src={healthcareDetails.vendor.imageUrl || placeholderImage}
              alt={healthcareDetails.vendor.name}
            />
            <VendorInfo>
              <VendorName>{healthcareDetails.vendor.name}</VendorName>
              <HeaderTagline vendor={healthcareDetails.vendor} />
              {healthcareDetails.vendor.specializations &&
                healthcareDetails.vendor.specializations.length > 0 && (
                  <SpecializationsContainer>
                    {healthcareDetails.vendor.specializations.map(
                      (spec, index) => (
                        <SpecializationChip
                          key={index}
                          label={spec}
                          size="small"
                        />
                      )
                    )}
                  </SpecializationsContainer>
                )}
              {healthcareDetails.vendor.address && (
                <VendorAddress>
                  {healthcareDetails.vendor.address}
                </VendorAddress>
              )}
            </VendorInfo>
            <ShareButton onClick={handleShareClick}>
              <ShareIcon />
            </ShareButton>
          </VendorInfoOverlay>
        </BannerContainer>

        <div style={{ padding: "0 16px" }}>
          {/* Which branch this storefront orders from, and the dialog to
              choose another. Nothing for a one-location pharmacy. */}
          <LocationPicker
            locations={pharmacyLocations}
            currentId={outletId || pharmacy?._id}
            vendorName={pharmacyName}
            onChoose={chooseLocation}
            confirmChoice={cartBoundElsewhere}
            deliveryPoint={deliveryPoint}
            accent="#E91E63"
          />
          <RestaurantAvailabilityBanner availability={availability} />
          <CoverageBanner
            variant="block"
            deliverable={coverage.deliverable}
            reason={coverage.reason}
            distanceKm={coverage.distanceKm}
            radius={coverage.radius}
            hasLocation={coverage.hasLocation}
            status={coverage.status}
            error={coverage.error}
            onRetry={coverage.refresh}
            onChangeLocation={() => setShowLocationPrompt(true)}
          />
        </div>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleShareClose}
        >
          <MenuItem onClick={handleCopyLink}>
            <ListItemIcon>
              <LinkIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy Link</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleWhatsAppShare}>
            <ListItemIcon>
              <WhatsAppIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Share on WhatsApp</ListItemText>
          </MenuItem>
        </Menu>

        {sectionsWithProducts.length > 0 && (
          <SectionsNav ref={sectionsNavRef}>
            <SectionsContainer>
              {sectionsWithProducts.map((section) => (
                <SectionTab
                  key={section._id}
                  active={activeSection === `section-${section._id}`}
                  onClick={handleSectionClick(`section-${section._id}`)}
                >
                  {section.title}
                </SectionTab>
              ))}
            </SectionsContainer>
          </SectionsNav>
        )}

        <ContentContainer data-sections-container>
          {sectionsWithProducts.map((section) => (
            <Section key={section._id} id={`section-${section._id}`}>
              <SectionHeader>
                <SectionTitle>{section.title}</SectionTitle>
                {section.description && (
                  <SectionDescription>{section.description}</SectionDescription>
                )}
              </SectionHeader>
              <ProductsGrid>
                {section.products.map((product) => {
                  const cartItem = cart[product._id];
                  const isInCart = Boolean(cartItem && (Number(cartItem.quantity) || 0) > 0);
                  const cartQuantity = cartItem ? Number(cartItem.quantity) || 0 : 0;
                  /* The posture, once per card. A quote-only line has no
                     published price to discount, so it can never be on offer. */
                  const quoteOnly = isQuoteOnly(product);
                  const shownPrice = publishedPrice(product);
                  const unpriced = !quoteOnly && shownPrice === null;
                  const onOffer = !quoteOnly && hasSalePrice(product);
                  // Sold out or switched off at this branch (4 Oct 2026).
                  const branchBlocked = !quoteOnly && blockedHere(product);
                  return (
                  <ProductCard
                    key={product._id}
                    isInCart={isInCart}
                    onClick={() => handleProductClick(product)}
                    style={{ opacity: coverage.isOutOfRange || branchBlocked ? 0.6 : 1 }}
                  >
                    <ProductImageContainer>
                      <CrawlLink
                        to={productPathUnder(`/healthcare/shop/${idParam}`, product)}
                        onNavigate={() => handleProductClick(product)}
                        style={{ display: "contents" }}
                      >
                        <ProductImage
                          src={product.image || placeholderImage}
                          alt={product.name}
                          onError={(e) => {
                            e.target.src = placeholderImage;
                          }}
                        />
                      </CrawlLink>
                      {onOffer && (
                        <DealTag
                          discount={product.discount}
                          size="m"
                          radius={12}
                        />
                      )}
                      {coverage.isOutOfRange && (
                        <OutOfRangeBadge $besideDeal={onOffer}>
                          Outside delivery area
                        </OutOfRangeBadge>
                      )}
                      {isInCart && (
                        <CartQuantityBadge>{cartQuantity}</CartQuantityBadge>
                      )}
                      {product.requiresPrescription && (
                        <PrescriptionBadge $besideDeal={onOffer}>
                          <span>℞</span> Prescription Required
                        </PrescriptionBadge>
                      )}
                      {product.ageRestriction > 0 && (
                        <AgeRestrictionBadge>
                          {product.ageRestriction}+ Only
                        </AgeRestrictionBadge>
                      )}
                    </ProductImageContainer>
                    <ProductInfo>
                      <ProductName>
                        <CrawlLink
                          to={productPathUnder(`/healthcare/shop/${idParam}`, product)}
                          onNavigate={() => handleProductClick(product)}
                        >
                          {product.name}
                        </CrawlLink>
                      </ProductName>
                      {branchBlocked && (
                        <BranchAvailabilityBadge
                          compact
                          product={product}
                          branchName={hereName}
                          locations={pharmacyLocations}
                          vendorName={pharmacyName}
                          onGetItFrom={getItFrom.request}
                          accent="#E91E63"
                        />
                      )}
                      {product.description && (
                        <ProductDescription>
                          {product.description.substring(0, 80)}
                          {product.description.length > 80 && "..."}
                        </ProductDescription>
                      )}
                      <ProductFooter>
                        {/* Three states, not two: quote-only is a commercial
                            posture with a price behind a conversation, unpriced
                            is a SKU nobody has costed yet, and only the third
                            gets a number. */}
                        {quoteOnly ? (
                          <ProductPrice>{PRICE_ON_REQUEST_LABEL}</ProductPrice>
                        ) : unpriced ? (
                          <ProductPrice>{PRICE_UNAVAILABLE_LABEL}</ProductPrice>
                        ) : (
                          <PriceCut
                            component={ProductPrice}
                            price={shownPrice}
                            listPrice={product.listPrice}
                            salePrice={product.salePrice}
                            format={formatCurrency}
                          />
                        )}
                        {isInCart ? (
                          <InCartPill>
                            <CheckCircleIcon style={{ fontSize: "0.85rem" }} />
                            {cartQuantity} in cart
                          </InCartPill>
                        ) : (
                          <>
                            {product.productType === "PRODUCT" && (
                              <StockIndicator inStock={product.countInStock > 0}>
                                {product.countInStock > 0 ? (
                                  <>
                                    <CheckCircleIcon fontSize="small" /> In Stock
                                  </>
                                ) : (
                                  "Out of Stock"
                                )}
                              </StockIndicator>
                            )}
                            {product.productType === "SERVICE" && (
                              <ServiceIndicator>
                                <MedicalServicesIcon fontSize="small" /> Service
                              </ServiceIndicator>
                            )}
                          </>
                        )}
                      </ProductFooter>
                    </ProductInfo>
                  </ProductCard>
                  );
                })}
              </ProductsGrid>
            </Section>
          ))}
        </ContentContainer>

        {cartCount > 0 && (
          <CartButton onClick={() => setIsCartVisible(!isCartVisible)}>
            <Badge badgeContent={cartCount} color="success">
              <ShoppingCartIcon />
            </Badge>
            <span style={{ marginLeft: "8px" }}>View Cart</span>
          </CartButton>
        )}

        <HealthcareCart
          isVisible={isCartVisible}
          onClose={() => setIsCartVisible(false)}
          vendorId={id}
        />
        {/* "Changes at <branch>", after the cart moved to another branch. */}
        <BranchCartReviewDialog {...branchDialogProps} />

        {selectedProduct && (
          <HealthcareProductDialog
            product={selectedProduct}
            onClose={handleCloseDialog}
            vendorId={id}
            vendor={selectedActiveVendor || healthcareDetails?.vendor}
            outletId={outletId}
            branchName={hereName}
            cartBranchName={nameOfLocation(cartLocation)}
            onCartMoved={reviewNextCheck}
            locations={pharmacyLocations}
            onGetItFrom={(picked) => {
              /* Leave the product's address for the storefront's in place
                 (no Back in flight to race the branch switch), so a confirmed
                 switch opens the storefront and a cancelled one is simply
                 the storefront again. */
              openedByTapRef.current = false;
              setSelectedProduct(null);
              history.replace(withOutletQuery(`/healthcare/shop/${idParam}`, outletIdParam));
              getItFrom.request(picked);
            }}
          />
        )}
        {/* "Get it from <branch>" with items from another branch in the cart. */}
        <OutletSwitchDialog
          open={Boolean(getItFrom.pending)}
          fromLabel={nameOfLocation(cartLocation)}
          toLabel={locationName(getItFrom.pending, pharmacyName)}
          onCancel={getItFrom.cancel}
          onConfirm={getItFrom.confirm}
        />

        {showLocationPrompt && (
          <LocationPrompt open onClose={() => setShowLocationPrompt(false)} />
        )}

        {/* Healthcare vendor-level reviews. */}
        {id && (
          <Reviews
            subjectType="vendor_healthcare"
            subjectId={id}
            title={`Reviews for ${healthcareDetails?.vendor?.name || "this vendor"}`}
          />
        )}
        <VendorWhatsAppFloat
          phone={selectedActiveVendor?.phone || healthcareDetails?.vendor?.phone}
          vendorName={selectedActiveVendor?.name || healthcareDetails?.vendor?.name}
          vendorType="healthcare"
          pageUrl={`${SITE_ORIGIN}/healthcare/shop/${buildVendorParam(selectedActiveVendor?.name || healthcareDetails?.vendor?.name, id)}`}
        />
      </PageContainer>
    </>
  );
};

/* A storefront that could not load says so in place and offers the load again.
   Sending the customer to "/" read as a broken app and lost them with no
   explanation. Mirrors the EventsPage state card, the same shape the rest of
   the web client uses for "could not load, try again". */
export const HealthcareLoadError = ({ onRetry }) => (
  <StateCard>
    <StateIcon>
      <CloudOffIcon />
    </StateIcon>
    <StateTitle>We could not load this pharmacy</StateTitle>
    <StateBody>
      Check your connection and try again. Nothing has changed in your cart or
      your account.
    </StateBody>
    <StateCta type="button" onClick={onRetry}>
      Try again
    </StateCta>
  </StateCard>
);

export default HealthcareShopPage;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
  padding-bottom: 80px;
  padding-top: var(--lusuku-nav-h, 130px); /* Add padding for fixed navbar */

  @media (max-width: 850px) {
    padding-top: var(--lusuku-nav-h, 130px);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  font-size: 1.2rem;
  color: #e91e63;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  font-size: 1.2rem;
  color: #d32f2f;
`;

const StateCard = styled.div`
  max-width: 460px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #edeff1;
  border-radius: 16px;
  padding: 34px 26px;
  text-align: center;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.07);
`;

const StateIcon = styled.div`
  width: 68px;
  height: 68px;
  margin: 0 auto 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fdf1de;
  svg {
    width: 32px;
    height: 32px;
    color: #a35c00;
  }
`;

const StateTitle = styled.div`
  font-size: 17px;
  font-weight: 800;
  color: #1f2937;
`;

const StateBody = styled.p`
  margin: 8px 0 0;
  font-size: 13.5px;
  line-height: 20px;
  color: #6b7280;
`;

const StateCta = styled.button`
  margin-top: 22px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 10px;
  background: #83b524;
  color: #fff;
  font-size: 14.5px;
  font-weight: 700;
  padding: 12px 22px;
  cursor: pointer;
  box-shadow: 0 3px 6px rgba(77, 122, 16, 0.28);
  &:hover {
    background: #78a520;
  }
`;

const BannerContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 1440px;
  aspect-ratio: 1440 / 175;
  margin: 0 auto;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 200px;
    aspect-ratio: auto;
  }
`;

const BannerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const VendorInfoOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  padding: 20px;
  display: flex;
  align-items: flex-end;
  gap: 15px;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const VendorAvatar = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
`;

/* The vendor's own tagline under the name, in the header's white type.
   Nothing renders when the vendor has not written one
   (components/VendorTagline.jsx). && outranks the header's own p rule. */
const HeaderTagline = styled(VendorTagline)`
  && {
    margin: 4px 0 6px;
    max-width: 560px;
    font-size: 0.95rem;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.92);
  }
`;

const VendorInfo = styled.div`
  flex: 1;
  color: white;
`;

const VendorName = styled.h1`
  font-size: 1.8rem;
  margin: 0 0 8px 0;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const SpecializationsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
`;

const SpecializationChip = styled(Chip)`
  background-color: #e91e63 !important;
  color: white !important;
  font-size: 0.75rem !important;
  height: 24px !important;
`;

const VendorAddress = styled.p`
  font-size: 0.9rem;
  margin: 0;
  opacity: 0.9;
`;

const ShareButton = styled(IconButton)`
  color: white !important;
  background-color: rgba(255, 255, 255, 0.2) !important;

  &:hover {
    background-color: rgba(255, 255, 255, 0.3) !important;
  }
`;

const SectionsNav = styled.div`
  /* Pinned under the Navbar at the height it actually rendered. 64px was
     under it on every screen size. See utils/stickyStack. */
  ${stickyUnderNav}
  background-color: white;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    top: 56px;
  }
`;

const SectionsContainer = styled.div`
  display: flex;
  padding: 0 20px;
  min-width: min-content;
`;

const SectionTab = styled.button`
  background: none;
  border: none;
  padding: 16px 20px;
  font-size: 0.95rem;
  font-weight: ${(props) => (props.active ? "600" : "400")};
  color: ${(props) => (props.active ? "#E91E63" : "#333")};
  border-bottom: 3px solid
    ${(props) => (props.active ? "#E91E63" : "transparent")};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    color: #e91e63;
    background-color: rgba(233, 30, 99, 0.05);
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px 10px;
  }

  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const Section = styled.div`
  margin-bottom: 40px;
  padding-top: 20px;
  ${sectionScrollMargin}

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 18px 20px;
  background-color: #ffffff;
  border-left: 5px solid #e91e63;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 14px 16px;
    margin-bottom: 20px;
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.6em;
  font-weight: 700;
  color: #1a1a1a;
  position: relative;
  white-space: nowrap;
  padding-right: 20px;

  &:after {
    content: "";
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    background-color: #e91e63;
    border-radius: 50%;
    box-shadow: 0 0 0 3px rgba(233, 30, 99, 0.2);
  }

  @media (max-width: 768px) {
    font-size: 1.3em;
    padding-right: 16px;
  }
`;

const SectionDescription = styled.p`
  margin: 0;
  font-size: 0.9em;
  font-style: italic;
  color: #666;
  font-weight: 400;
  line-height: 1.4;
  flex: 1;
  padding-left: 16px;
  border-left: 2px solid #f0f0f0;
  position: relative;

  &:before {
    content: "";
    position: absolute;
    left: -6px;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    height: 10px;
    background-color: #e91e63;
    border-radius: 50%;
    border: 2px solid white;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  padding: 8px 4px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const ProductCard = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  height: 100%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
    border-color: #e91e63;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const ProductImageContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 75%;
  overflow: hidden;
`;

const ProductImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #f8f8f8;
  display: block;
  margin: 0;
  padding: 0;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const PrescriptionBadge = styled.div`
  position: absolute;
  top: 8px;
  /* The deal stub (48 wide) owns the top-left corner; chips step right. */
  left: ${(props) => (props.$besideDeal ? "56px" : "8px")};
  background-color: #d32f2f;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 2;

  span {
    font-size: 1rem;
  }

  @media (max-width: 768px) {
    font-size: 0.65rem;
    padding: 3px 6px;
    top: 6px;
    left: ${(props) => (props.$besideDeal ? "54px" : "6px")};

    span {
      font-size: 0.85rem;
    }
  }
`;

const OutOfRangeBadge = styled.div`
  position: absolute;
  top: 8px;
  left: ${(props) => (props.$besideDeal ? "56px" : "8px")};
  background: rgba(138, 43, 6, 0.92);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 4px 8px;
  border-radius: 6px;
  z-index: 2;
  pointer-events: none;
`;

const AgeRestrictionBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: #ff9800;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 0.65rem;
    padding: 3px 6px;
    top: 6px;
    right: 6px;
  }
`;

const ProductInfo = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;

  @media (max-width: 768px) {
    padding: 10px;
    gap: 4px;
  }
`;

const ProductName = styled.h3`
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  padding: 0;
  color: #1a1a1a;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ProductDescription = styled.p`
  font-size: 12px;
  color: #666;
  margin: 0;
  padding: 0;
  line-height: 1.4;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const ProductFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 4px;
  ${cardFooterRow}
`;

const ProductPrice = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #e91e63;
  ${cardPriceRow}
  ${cardPriceOwnsItsRow}

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const StockIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  color: ${(props) => (props.inStock ? "#4caf50" : "#d32f2f")};
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
`;

const ServiceIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  color: #e91e63;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
`;

const CartQuantityBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: #e91e63;
  color: white;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  z-index: 3;
  box-shadow: 0 2px 6px rgba(233, 30, 99, 0.5);
  border: 2px solid white;
`;

const InCartPill = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: #e8f5e9;
  color: #2e7d32;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 12px;
  border: 1px solid #a5d6a7;
  white-space: nowrap;
`;

const CartButton = styled.button`
  position: fixed;
  bottom: 96px;
  right: 20px;
  background-color: #e91e63;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 15px 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.12),
    0 10px 20px rgba(233, 30, 99, 0.28),
    0 20px 40px rgba(233, 30, 99, 0.14),
    0 1px 3px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  z-index: 1400;

  &:hover {
    background-color: #c2185b;
    transform: translateY(-3px) scale(1.03);
    box-shadow:
      0 6px 10px rgba(0, 0, 0, 0.14),
      0 16px 30px rgba(233, 30, 99, 0.36),
      0 28px 50px rgba(233, 30, 99, 0.18),
      0 1px 3px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    bottom: 76px;
    right: 15px;
    padding: 12px 20px;
    font-size: 0.9rem;
  }
`;
